import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useState, useCallback } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { usePRD2040_R01Store, PRD2040_R01TabType } from "./Store_PRD2040_R01";
import { defaultPRD2040_R01BomDto, defaultPRD2040_R01MountDto, PRD2040_R01BomDto, PRD2040_R01MountDto } from "./Dto_PRD2040_R01";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { getDefaultRowIndexColumnConfig, defaultColDef } from "src/utils/grid/gridStyleUtils";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";

const BOM_COLUMN_STATE_KEY = 'PRD2040_R01-bom-grid-column-state';
const MOUNT_COLUMN_STATE_KEY = 'PRD2040_R01-mount-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<any> | null>;
}

export const Table_PRD2040_R01 = (props: Props) => {
    const { gridRef } = props;

    const {
        activeTab,
        setActiveTab,
        tabData,
        tabSelectedId,
        setTabSelectedId,
        initTabSelectedId,
        tabColumnState,
        setTabColumnState,
        tabScrollTop,
        setTabScrollTop,
    } = usePRD2040_R01Store();

    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const gridData: any[] = tabData[activeTab];
    const selectedId = tabSelectedId[activeTab];
    const columnState = tabColumnState[activeTab];
    const scrollTop = tabScrollTop[activeTab];
    const columnStateKey = activeTab === 'BOM' ? BOM_COLUMN_STATE_KEY : MOUNT_COLUMN_STATE_KEY;
    const defaultRowData = activeTab === 'BOM' ? defaultPRD2040_R01BomDto : defaultPRD2040_R01MountDto;
	
	const headerNames = useMemo((): Record<string, string> => {
		if (activeTab === 'BOM') {
			return {
				rowIndex: '',
				childItemcd: '품목코드',
				itemNm: '품명',
				itemSpec: '규격',
				locationNm: '보관위치',
				mountQty: '비교품소요량',
				bomQty: '지시품 소요량',
				diffQty: '비고',
			};
		}
		return {};
	}, [activeTab]);

    const {
        getRowId,
        settingRowIndex,
        createRowClassRules,
        paddedGridData,
    } = useGridSettingContent<any>({
        gridRef,
        key: 'valueIndex',
        gridData,
        defaultGridData: defaultRowData,
        selectedRow: gridData.find((item: any) => String(item.valueIndex) === selectedId),
    });

    const bomColumnDefs: ColDef<any>[] = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(),
        { headerName: '품목코드', colId: 'childItemcd', field: 'childItemcd', width: 130 },
        { headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 200 },
        { headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150 },
		{ headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120, },
        {
            headerName: '지시품 소요량', colId: 'bomQty', field: 'bomQty', width: 120,
            type: 'numericColumn',
            valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
        },
        {
            headerName: '비교품소요량', colId: 'mountQty', field: 'mountQty', width: 120,
            type: 'numericColumn',
            valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
        },
        {
            headerName: '비고', colId: 'diffQty', field: 'diffQty', width: 80,
            valueFormatter: (params) => {
                if (!params.data?.isMismatch) return '';
                const diff = params.data.diffQty || 0;
                return diff > 0 ? `+${diff}` : `${diff}`;
            },
        },
    ], []);

    const mountColumnDefs: ColDef<any>[] = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(),
        { headerName: '구분', colId: 'topBottom', field: 'topBottom', width: 70 },
        { headerName: '로케이션', colId: 'coordinateCd', field: 'coordinateCd', width: 100 },
        { headerName: '변경사항', colId: 'changeType', field: 'changeType', width: 100 },
        { headerName: '비교품목 자재', colId: 'mountItemNm', field: 'mountItemNm', width: 200 },
        { headerName: '지시품목 자재', colId: 'bomItemNm', field: 'bomItemNm', width: 200 },
        { headerName: 'X', colId: 'xcoordinate', field: 'xcoordinate', width: 80, type: 'numericColumn' },
        { headerName: 'Y', colId: 'ycoordinate', field: 'ycoordinate', width: 80, type: 'numericColumn' },
        { headerName: 'R', colId: 'orient', field: 'orient', width: 60, type: 'numericColumn' },
    ], []);

    const baseColumnDefs = useMemo(
        () => (activeTab === 'BOM' ? bomColumnDefs : mountColumnDefs)
    , [activeTab, bomColumnDefs, mountColumnDefs]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
    , [baseColumnDefs]);

    const columnDefs = useMemo(() => {
        if (columnState === null) {
            return baseColumnDefs;
        }
        return GridCustomColumnDefs<PRD2040_R01BomDto | PRD2040_R01MountDto>({
            baseColumnDefs,
            columnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [baseColumnDefs, columnState, settingRowIndex, headerNames]);

    const pinnedBottomRow = useMemo(() => {
        if (activeTab === 'BOM') {
            return [{
                childItemcd: gridData.length.toLocaleString() + "건",
                mountQty: gridData.reduce((sum: number, item: any) => sum + (item.mountQty || 0), 0),
                bomQty: gridData.reduce((sum: number, item: any) => sum + (item.bomQty || 0), 0),
            }];
        }
        return [{
            topBottom: gridData.length.toLocaleString() + "건",
        }];
    }, [activeTab, gridData]);

    const normalizedSelectedIds = selectedId ? [Number(selectedId)] : [];

    const rowClassRules = useMemo(() => createRowClassRules<any>({
        idKey: 'valueIndex',
        selectedList: normalizedSelectedIds,
        deletedList: [],
    }), [createRowClassRules, normalizedSelectedIds]);

    const handleRowClick = useCallback((e: any) => {
        if (e.node?.rowPinned === 'bottom') return;
        const row = e.data;
        if (!row) return;
        const id = String(row.valueIndex);
        setTabSelectedId(activeTab, selectedId === id ? undefined : id);
    }, [activeTab, selectedId, setTabSelectedId]);

    return (
        <div className="w-full h-full flex flex-col">
            {/* 탭 버튼 섹션 */}
            <div
                className="flex items-end mb-[0.1px] bg-white h-[25px]"
                role="tablist"
                aria-label="BOM 검토 탭"
            >
                <button
                    onClick={() => setActiveTab('BOM')}
                    role="tab"
                    aria-selected={activeTab === 'BOM'}
                    className={`h-[26px] px-3 text-[12px] font-medium transition-colors border border-gray-300 border-b-0
                        ${activeTab === 'BOM'
                            ? 'bg-white text-black-700 -mb-px shadow-sm'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                    `}
                >
                    BOM 검토
                </button>
                <button
                    onClick={() => setActiveTab('MOUNT')}
                    role="tab"
                    aria-selected={activeTab === 'MOUNT'}
                    className={`h-[26px] px-3 text-[12px] font-medium transition-colors border border-gray-300 border-b-0
                        ${activeTab === 'MOUNT'
                            ? 'bg-white text-black-700 -mb-px shadow-sm'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                    `}
                >
                    실장좌표 검토
                </button>
            </div>

            {/* 그리드 섹션 */}
            <div className="flex-1">
                <GridContextWrapper<any>
                	enableCellCalculation
                    gridRef={gridRef}
                    paddedGridData={paddedGridData}
                    initSelected={() => initTabSelectedId(activeTab)}
                    defaultRowData={defaultRowData}
                    allSelected={() => {}}
                    baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
                    defaultColumnState={defaultColumnState}
                    setColumnState={(state) => setTabColumnState(activeTab, state)}
                    localStorageKey={columnStateKey}
                    menuItems={undefined}
                >
                    <Grid_Touch<any>
                        key={activeTab}
                        ref={gridRef}
                        rowData={paddedGridData}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        getRowId={getRowId}
                        onCellKeyDown={undefined}
                        rowClassRules={rowClassRules}
                        onRowClicked={handleRowClick}
                        pinnedBottomRowData={pinnedBottomRow}
                        setHoveredRowIndex={setHoveredRowIndex}
                        columnStateKey={columnStateKey}
                        setColumnState={(state) => setTabColumnState(activeTab, state)}
                        scrollTop={scrollTop}
                        setScrollTop={(top) => setTabScrollTop(activeTab, top)}
                    />
                </GridContextWrapper>
            </div>
        </div>
    );
};

export default Table_PRD2040_R01;
